// React-free i18n utilities (safe in server and client)

export type Lang = "en" | "fr" | "es" | "de";

const dictionaries: Record<Lang, Record<string, string>> = {
    en: {
        search: "Search",
        location: "Location",
        change: "Change",
        change_ellipsis: "Change…",
        all_listings: "All Listings",
        seller_listings: "Seller Listings",
        buyer_listings: "Buyer Listings",
        category: "Category",
        type: "Type",
        price_sats: "Price (sats)",
        price_btc: "Price (BTC)",
        min: "Min",
        max: "Max",
        newest: "Newest",
        oldest: "Oldest",
        highest_price: "Highest Price",
        lowest_price: "Lowest Price",
        closest: "Closest",
        no_results: "No results found",
        try_different: "Try different keywords or clear filters",
        apply: "Apply",
        clear_all: "Clear all",
        cancel: "Cancel",
        // close already defined above in base keys; avoid duplicate
        change_location: "Change location",
        radius: "Radius",
        enter_city: "Enter a city",
        title_hero_1: "Find better deals,",
        title_hero_2: "use better money.",
        how_it_works: "How it works",
        pricing: "Pricing",
        post_listing: "Post a listing",
        sign_in: "Sign in",
        // Homepage
        subheading: "The Bitcoin-native marketplace",
        search_placeholder: "Search bikes, ASICs, consoles…",
        choose_location: "Choose location",
        my_location: "My Location",
        featured: "Featured",
        latest: "Latest",
        services_label: "Services",
        no_goods_match: "No matches",
        try_widen_radius: "Try widening your radius, using different search terms, or clearing filters.",
        no_services_match: "No services match your search",
        try_adjust_filters: "Try adjusting your filters",
        loading_more: "Loading more…",
        no_more_results: "No more results",
        selected_location: "Selected location",
        set_location: "Set location",
        within_km_of: "Within {n} km of",
        // Footer
        legal: "Legal",
        services: "Services",
        company: "Company",
        prohibited_items: "Prohibited items",
        safety_tips: "Safety tips",
        safety: "Safety",
        terms: "Terms",
        privacy: "Privacy",
        escrow: "Escrow",
        about: "About",
        contact: "Contact",
        careers: "Careers",
        help: "Help",
        faq: "FAQ",
        support: "Support",
        made_with_love: "Made with ❤️ in Canada",
        footer_tagline: "⚡ Bitboard — in-app chat + Lightning escrow. Keep correspondence in-app; off-app contact is against our guidelines.",
        // Notifications
        notifications: "Messages",
        all: "All",
        unread: "Unread",
        messages: "Messages",
        updates: "Updates",
        system: "System",
        mark_as_read: "Mark as read",
        mark_all_as_read: "Mark all as read",
        delete: "Delete",
        view_details: "View Details",
        no_notifications: "No messages yet",
        no_notifications_found: "No messages found",
        you_are_caught_up: "You're all caught up! No messages at the moment.",
        no_filter_notifications: "No {filter} found.",
        notification_summary: "Message Summary",
        total: "Total",
        stay_updated: "Your message center for chats, updates, and system announcements",
        message_center_description: "Your message center for chats, updates, and system announcements",
        view_all_notifications: "View all messages",
        // Profile
        sign_out: "Sign Out",
        verified: "Verified",
        reputation: "Reputation",
        excellent: "Excellent",
        member_since: "Member Since",
        active_listings: "Active Listings",
        create_listing: "Create a Listing",
        no_listings_yet: "No Listings Yet",
        no_listings_owner: "You don't have any active listings right now",
        no_listings_visitor: "This user does not have any active listings right now",
        grid: "Grid",
        list: "List",
        description: "Description",
        report_listing: "Report listing",
        close: "Close",
        // Listing modal
        message_seller: "Message seller",
        send_message: "Send a Message",
        back_to_listing: "Back to Listing",
        share_listing: "Share listing",
        listing_warning: "For safety, keep correspondence in-app.",
        learn_more: "Learn more",
        results: "results",
        all_listings_globally: "Worldwide",
        all_of_country: "All of {country}",
        all_listings_in_country: "All Listings in {country}",
        nationally: "Nationally",
        globally: "Globally",
        // Categories
        cat_all: "All",
        cat_electronics: "Electronics",
        cat_mining_gear: "Mining Gear",
        cat_home_garden: "Home & Garden",
        cat_sports_bikes: "Sports & Bikes",
        cat_tools: "Tools",
        cat_games_hobbies: "Games & Hobbies",
        cat_furniture: "Furniture",
        cat_services: "Services",
        // Menu
        menu_display_prices_in: "Display prices in:",
        menu_display_theme: "Display Theme:",
        menu_layout_view: "Layout View:",
        // Types (singular labels)
        selling: "Selling",
        looking_for: "Looking For",
        verified_tooltip: "User has verified their identity",

        // About page
        about_bitsbarter: "About bitsbarter",
        bitcoin_native_marketplace: "The Bitcoin-native marketplace for local classifieds",
        about_description_1: "bitsbarter is a revolutionary local classifieds platform built specifically for the Bitcoin ecosystem. We believe that local commerce should be fast, secure, and free from the limitations of traditional payment systems.",
        about_description_2: "Our platform combines the convenience of local classifieds with the power of Bitcoin's Lightning Network, enabling instant, low-fee transactions while maintaining the security and privacy that Bitcoin users expect.",
        secure: "Secure",
        secure_description: "Built-in Lightning escrow ensures safe transactions between buyers and sellers",
        fast: "Fast",
        fast_description: "Lightning Network enables instant payments and settlements",
        local: "Local",
        local_description: "Connect with people in your community for face-to-face transactions",
        our_mission: "Our Mission",
        mission_description: "We're building a future where local commerce is powered by sound money. By leveraging Bitcoin's Lightning Network, we're creating a marketplace that's not only more efficient than traditional classifieds but also more aligned with the principles of financial sovereignty and privacy.",
        join_revolution: "Join the Revolution",
        start_today: "Start buying and selling with Bitcoin today",
        get_started: "Get Started",

        // Contact page
        contact_us: "Contact Us",
        contact_description: "Have questions or need support? We're here to help.",
        get_in_touch: "Get in Touch",
        response_time: "We typically respond within 24 hours",
        join_community: "Join our community discussions",
        community_help: "Get help from other users and our team",
        check_help_center: "Check our help center first",
        help_center_info: "Many questions are answered in our guides",
        business_hours: "Business Hours",
        business_hours_details: "Monday - Friday: 9:00 AM - 6:00 PM EST\nSaturday: 10:00 AM - 4:00 PM EST\nSunday: Closed",
        name: "Name",
        your_name: "Your name",
        subject: "Subject",
        select_subject: "Select a subject",
        general_inquiry: "General Inquiry",
        technical_support: "Technical Support",
        billing_question: "Billing Question",
        partnership: "Partnership",
        other: "Other",
        message: "Message",
        tell_us_how: "Tell us how we can help...",

        // FAQ page
        frequently_asked_questions: "Frequently Asked Questions",
        faq_description: "Find answers to the most common questions about using bitsbarter",
        getting_started: "Getting Started",
        how_create_account: "How do I create an account?",
        create_account_answer: "Click the 'Sign In' button in the header and choose 'Create Account'. You'll need to provide a username, email, and password. We also support social login options for convenience.",
        is_bitsbarter_free: "Is bitsbarter free to use?",
        bitsbarter_free_answer: "Yes, creating an account and browsing listings is completely free. There are only fees when using the Lightning escrow service for transactions (typically 1-2%).",
        need_bitcoin_wallet: "Do I need a Bitcoin wallet?",
        bitcoin_wallet_answer: "Yes, you'll need a Lightning Network compatible Bitcoin wallet to use the escrow service and make payments. Popular options include Phoenix, Breez, and Muun.",
        buying_selling: "Buying & Selling",
        how_create_listing: "How do I create a listing?",
        create_listing_answer: "Click 'Post a listing' in the header, fill out the form with details about your item or service, set your price in sats or BTC, add photos, and publish. Your listing will be visible to users in your area.",
        how_contact_seller: "How do I contact a seller?",
        contact_seller_answer: "Click on any listing and use the 'Message seller' button. This opens our in-app chat where you can discuss details, negotiate price, and coordinate meetups safely.",
        can_negotiate_prices: "Can I negotiate prices?",
        negotiate_prices_answer: "Absolutely! Use the in-app chat to negotiate with sellers. Many sellers are open to offers, especially for items that have been listed for a while.",
        lightning_escrow: "Lightning Escrow",
        how_lightning_escrow_works: "How does Lightning escrow work?",
        lightning_escrow_answer: "When you agree to buy something, the buyer sends Bitcoin to a Lightning escrow smart contract. The funds are held securely until the buyer confirms receipt of the item, then automatically released to the seller.",
        what_if_problem_purchase: "What if there's a problem with my purchase?",
        problem_purchase_answer: "If there's an issue, contact our support team immediately. The escrow funds remain locked until both parties reach an agreement or we mediate the dispute based on evidence provided.",
        are_escrow_fees_refundable: "Are escrow fees refundable?",
        escrow_fees_answer: "Escrow fees are non-refundable as they cover the Lightning Network transaction costs and platform maintenance. However, if a transaction is cancelled before funds are sent to escrow, no fees are charged.",
        safety_security: "Safety & Security",
        is_safe_meet_strangers: "Is it safe to meet with strangers?",
        meet_strangers_answer: "We recommend meeting in public places during daylight hours. Always verify the other person's identity through our verification system and use the in-app chat to coordinate. Never share personal contact information outside the app.",
        what_if_feel_unsafe: "What should I do if I feel unsafe?",
        feel_unsafe_answer: "Trust your instincts. If you feel unsafe at any point, leave immediately and contact our support team. We take safety seriously and will investigate any reported incidents.",
        how_report_suspicious: "How do I report suspicious activity?",
        report_suspicious_answer: "Use the 'Report' button on any listing or contact our support team directly. We investigate all reports and take appropriate action to maintain a safe community.",
        technical_issues: "Technical Issues",
        app_not_loading: "The app isn't loading properly",
        app_not_loading_answer: "Try refreshing the page or clearing your browser cache. If the problem persists, check your internet connection and try using a different browser. Contact support if issues continue.",
        cant_upload_photos: "I can't upload photos to my listing",
        upload_photos_answer: "Make sure your photos are in JPG, PNG, or WebP format and under 10MB each. If you're still having issues, try using a different browser or device.",
        lightning_payment_failed: "My Lightning payment failed",
        lightning_payment_answer: "Check that your Lightning wallet has sufficient funds and is properly connected. Network congestion can sometimes cause delays. If the issue persists, contact our support team.",
        still_have_questions: "Still Have Questions?",
        cant_find_answer: "Can't find the answer you're looking for? Our support team is here to help.",
        contact_support: "Contact Support",
        visit_help_center: "Visit Help Center",

        // Help page
        help_center: "Help Center",
        help_description: "Find answers to common questions and learn how to use bitsbarter",
        learn_escrow_system: "Learn how our secure escrow system works",
        stay_safe_buying_selling: "Stay safe when buying and selling",
        get_help_support_team: "Get help from our support team",
        lightning_escrow_help_answer: "Lightning escrow uses smart contracts on the Bitcoin Lightning Network to hold funds securely until both parties complete the transaction. The buyer sends Bitcoin to the escrow, and once they confirm receipt of the item, the funds are automatically released to the seller.",
        what_if_dispute: "What if there's a dispute?",
        dispute_help_answer: "In case of disputes, our support team can mediate. The escrow funds remain locked until both parties reach an agreement or we make a determination based on evidence provided.",
        are_there_escrow_fees: "Are there fees for using escrow?",
        escrow_fees_help_answer: "Yes, there's a small fee (typically 1-2%) for using the escrow service. This covers the Lightning Network transaction costs and platform maintenance.",
        create_listing_help_answer: "Click the 'Post a listing' button in the header, fill out the form with details about your item or service, set your price in sats or BTC, and publish. Your listing will be visible to users in your area.",
        meet_strangers_help_answer: "We recommend meeting in public places during daylight hours. Always verify the other person's identity through our verification system and use the in-app chat to coordinate meetups. Never share personal contact information outside the app.",
        what_payment_methods: "What payment methods are accepted?",
        payment_methods_answer: "We only accept Bitcoin payments through the Lightning Network. This ensures fast, secure, and low-fee transactions. You'll need a Lightning wallet to use the platform.",
        for_buyers: "For Buyers",
        browse_listings_area: "Browse listings in your area",
        use_filters_find: "Use filters to find what you need",
        contact_sellers_chat: "Contact sellers through in-app chat",
        agree_terms_escrow: "Agree on terms and use escrow",
        meet_safely_complete: "Meet safely and complete the transaction",
        for_sellers: "For Sellers",
        create_detailed_listing: "Create a detailed listing",
        set_price_bitcoin: "Set your price in Bitcoin",
        respond_buyer_inquiries: "Respond to buyer inquiries",
        coordinate_safe_meetups: "Coordinate safe meetups",
        receive_payment_escrow: "Receive payment through escrow",
        still_need_help: "Still Need Help?",
        cant_find_looking_for: "Can't find what you're looking for? Our support team is here to help.",
        back_to_home: "Back to Home",

        // Support page
        support_center: "Support Center",
        support_description: "Get the help you need to use bitsbarter effectively and safely",
        account_issues: "Account Issues",
        account_issues_description: "Problems with login, registration, or account settings",
        payment_problems: "Payment Problems",
        payment_problems_description: "Issues with Lightning payments or escrow transactions",
        listing_problems: "Listing Problems",
        listing_problems_description: "Trouble creating, editing, or managing listings",
        safety_concerns: "Safety Concerns",
        safety_concerns_description: "Report suspicious activity or safety violations",
        technical_issues_description: "App not working, bugs, or performance problems",
        general_questions: "General Questions",
        general_questions_description: "Other questions about using the platform",
        get_help: "Get Help",
        urgent_safety_issues: "Urgent Safety Issues",
        urgent_safety_description: "If you're experiencing a safety concern or urgent issue, please contact us immediately. We prioritize safety-related support requests and will respond as quickly as possible.",
        contact_support_now: "Contact Support Now",
        self_service_resources: "Self-Service Resources",
        help_center_description: "Browse our comprehensive help articles and tutorials to find answers to common questions.",
        safety_guidelines: "Safety Guidelines",
        safety_guidelines_description: "Learn about our safety practices and tips for staying secure when buying and selling.",
        view_safety_tips: "View Safety Tips",
        expected_response_times: "Expected Response Times",
        safety_issues: "Safety Issues",
        within_2_hours: "Within 2 hours",
        payment_issues: "Payment Issues",
        within_4_hours: "Within 4 hours",
        general_support: "General Support",
        within_24_hours: "Within 24 hours",
        need_more_help: "Need More Help?",
        support_team_ready: "Our support team is ready to assist you with any questions or concerns.",
        browse_help_center: "Browse Help Center",
    },
    fr: {
        search: "Rechercher",
        location: "Lieu",
        change: "Modifier",
        change_ellipsis: "Modifier…",
        all_listings: "Toutes les annonces",
        seller_listings: "Annonces vendeur",
        buyer_listings: "Annonces acheteur",
        category: "Catégorie",
        type: "Type",
        price_sats: "Prix (sats)",
        price_btc: "Prix (BTC)",
        min: "Min",
        max: "Max",
        newest: "Plus récentes",
        oldest: "Plus anciennes",
        highest_price: "Prix le plus élevé",
        lowest_price: "Prix le plus bas",
        closest: "Le plus proche",
        no_results: "Aucun résultat",
        try_different: "Essayez d'autres mots-clés ou réinitialisez les filtres",
        apply: "Appliquer",
        clear_all: "Tout effacer",
        cancel: "Annuler",
        close: "Fermer",
        change_location: "Changer de lieu",
        radius: "Rayon",
        enter_city: "Entrez une ville",
        title_hero_1: "Faites de meilleures affaires,",
        title_hero_2: "utilisez une meilleure monnaie.",
        how_it_works: "Comment ça marche",
        pricing: "Tarifs",
        post_listing: "Publier une annonce",
        sign_in: "Se connecter",
        // Homepage
        subheading: "Le marché natif Bitcoin",
        search_placeholder: "Rechercher vélos, ASICs, consoles…",
        choose_location: "Choisir un lieu",
        my_location: "Ma position",
        featured: "À la une",
        latest: "Récents",
        services_label: "Services",
        no_goods_match: "Aucune annonce ne correspond à votre recherche",
        try_widen_radius: "Essayez d'élargir votre rayon ou de réinitialiser les filtres",
        no_services_match: "Aucun service ne correspond à votre recherche",
        try_adjust_filters: "Essayez d'ajuster vos filtres",
        loading_more: "Chargement…",
        no_more_results: "Plus de résultats",
        selected_location: "Lieu sélectionné",
        set_location: "Définir le lieu",
        within_km_of: "Dans un rayon de {n} km de",
        // Footer
        legal: "Légal",
        services: "Services",
        company: "Entreprise",
        prohibited_items: "Objets interdits",
        safety_tips: "Conseils de sécurité",
        safety: "Sécurité",
        terms: "Conditions",
        privacy: "Confidentialité",
        escrow: "Séquestre",
        about: "À propos",
        contact: "Contact",
        careers: "Carrières",
        help: "Aide",
        faq: "FAQ",
        support: "Support",
        made_with_love: "Fait avec ❤️ au Canada",
        footer_tagline: "⚡ Bitboard — messagerie intégrée + séquestre Lightning. Gardez les échanges dans l'application; le contact hors application est contraire à nos directives.",
        // Notifications
        notifications: "Messages",
        all: "Toutes",
        unread: "Non lues",
        messages: "Messages",
        updates: "Mises à jour",
        system: "Système",
        mark_as_read: "Marquer comme lu",
        mark_all_as_read: "Tout marquer comme lu",
        delete: "Supprimer",
        view_details: "Voir les détails",
        no_notifications: "Aucune notification pour le moment",
        no_notifications_found: "Aucune notification trouvée",
        you_are_caught_up: "Vous êtes à jour ! Aucune notification pour le moment.",
        no_filter_notifications: "Aucune notification {filter} trouvée.",
        notification_summary: "Résumé des notifications",
        total: "Total",
        stay_updated: "Restez informé de vos derniers messages, mises à jour et annonces système",
        view_all_notifications: "Voir toutes les notifications",
        // Profile
        sign_out: "Se déconnecter",
        verified: "Vérifié",
        reputation: "Réputation",
        excellent: "Excellente",
        member_since: "Membre depuis",
        active_listings: "Annonces actives",
        create_listing: "Créer une annonce",
        no_listings_yet: "Aucune annonce pour le moment",
        no_listings_owner: "Vous n'avez aucune annonce active en ce moment",
        no_listings_visitor: "Cet utilisateur n'a aucune annonce active en ce moment",
        grid: "Grille",
        list: "Liste",
        description: "Description",
        report_listing: "Signaler l'annonce",
        // Listing modal
        message_seller: "Contacter le vendeur",
        send_message: "Envoyer un message",
        back_to_listing: "Retour à l'annonce",
        share_listing: "Partager l'annonce",
        listing_warning: "Pour votre sécurité, gardez la correspondance dans l'application.",
        learn_more: "En savoir plus",
        results: "résultats",
        all_listings_globally: "Mondial",
        all_of_country: "Toutes les annonces en {country}",
        all_listings_in_country: "Toutes les annonces en {country}",
        nationally: "À l'échelle nationale",
        globally: "À l'échelle mondiale",
        // Categories
        cat_all: "Tous",
        cat_electronics: "Électronique",
        cat_mining_gear: "Matériel de minage",
        cat_home_garden: "Maison & Jardin",
        cat_sports_bikes: "Sports & Vélos",
        cat_tools: "Outils",
        cat_games_hobbies: "Jeux & Loisirs",
        cat_furniture: "Meubles",
        cat_services: "Services",
        // Menu
        menu_display_prices_in: "Afficher les prix en :",
        menu_display_theme: "Thème d'affichage :",
        menu_layout_view: "Affichage :",
        // Types (singular labels)
        selling: "Vente",
        looking_for: "Recherche",
        verified_tooltip: "L'utilisateur a vérifié son identité",
    },
    es: {
        search: "Buscar",
        location: "Ubicación",
        change: "Cambiar",
        change_ellipsis: "Cambiar…",
        all_listings: "Todos los anuncios",
        seller_listings: "Anuncios del vendedor",
        buyer_listings: "Anuncios del comprador",
        category: "Categoría",
        type: "Tipo",
        price_sats: "Precio (sats)",
        price_btc: "Precio (BTC)",
        min: "Mín",
        max: "Máx",
        newest: "Más recientes",
        oldest: "Más antiguas",
        highest_price: "Precio más alto",
        lowest_price: "Precio más bajo",
        closest: "Más cercano",
        no_results: "No se encontraron resultados",
        try_different: "Prueba otras palabras clave o limpia los filtros",
        apply: "Aplicar",
        clear_all: "Borrar todo",
        cancel: "Cancelar",
        close: "Cerrar",
        change_location: "Cambiar ubicación",
        radius: "Radio",
        enter_city: "Ingrese una ciudad",
        title_hero_1: "Consigue mejores ofertas,",
        title_hero_2: "usa mejor dinero.",
        how_it_works: "Cómo funciona",
        pricing: "Precios",
        post_listing: "Publicar anuncio",
        sign_in: "Iniciar sesión",
        // Homepage
        subheading: "El mercado nativo de Bitcoin",
        search_placeholder: "Buscar bicis, ASICs, consolas…",
        choose_location: "Elegir ubicación",
        my_location: "Mi ubicación",
        featured: "Destacados",
        latest: "Recientes",
        services_label: "Servicios",
        no_goods_match: "No hay artículos que coincidan con tu búsqueda",
        try_widen_radius: "Prueba ampliando el radio o limpiando los filtros",
        no_services_match: "No hay servicios que coincidan con tu búsqueda",
        try_adjust_filters: "Prueba ajustando tus filtros",
        loading_more: "Cargando…",
        no_more_results: "No hay más resultados",
        selected_location: "Ubicación seleccionada",
        set_location: "Establecer ubicación",
        within_km_of: "A {n} km de",
        // Footer
        legal: "Legal",
        services: "Servicios",
        company: "Empresa",
        prohibited_items: "Artículos prohibidos",
        safety_tips: "Consejos de seguridad",
        safety: "Seguridad",
        terms: "Términos",
        privacy: "Privacidad",
        escrow: "Escrow",
        about: "Acerca de",
        contact: "Contacto",
        careers: "Carreras",
        help: "Ayuda",
        faq: "FAQ",
        support: "Soporte",
        made_with_love: "Hecho con ❤️ en Canadá",
        footer_tagline: "⚡ Bitboard — chat en la app + escrow Lightning. Mantén la correspondencia en la app; el contacto fuera de la app va en contra de nuestras directrices.",
        // Notifications
        notifications: "Notificaciones",
        all: "Todas",
        unread: "No leídas",
        messages: "Mensajes",
        updates: "Actualizaciones",
        system: "Sistema",
        mark_as_read: "Marcar como leída",
        mark_all_as_read: "Marcar todas como leídas",
        delete: "Eliminar",
        view_details: "Ver detalles",
        no_notifications: "No hay notificaciones aún",
        no_notifications_found: "No se encontraron notificaciones",
        you_are_caught_up: "¡Estás al día! No hay notificaciones en este momento.",
        no_filter_notifications: "No se encontraron notificaciones {filter}.",
        notification_summary: "Resumen de notificaciones",
        total: "Total",
        stay_updated: "Mantente actualizado con tus últimos mensajes, actualizaciones y anuncios del sistema",
        view_all_notifications: "Ver todas las notificaciones",
        // Profile
        sign_out: "Cerrar sesión",
        verified: "Verificado",
        reputation: "Reputación",
        excellent: "Excelente",
        member_since: "Miembro desde",
        active_listings: "Anuncios activos",
        create_listing: "Crear un anuncio",
        no_listings_yet: "No hay anuncios aún",
        no_listings_owner: "No tienes anuncios activos en este momento",
        no_listings_visitor: "Este usuario no tiene anuncios activos en este momento",
        grid: "Cuadrícula",
        list: "Lista",
        description: "Descripción",
        report_listing: "Reportar anuncio",
        // Listing modal
        message_seller: "Enviar mensaje al vendedor",
        send_message: "Enviar mensaje",
        back_to_listing: "Volver al anuncio",
        share_listing: "Compartir anuncio",
        listing_warning: "Por seguridad, mantén la correspondencia en la app.",
        learn_more: "Más información",
        results: "resultados",
        all_listings_globally: "Mundial",
        all_of_country: "Todos los anuncios en {country}",
        all_listings_in_country: "Todos los anuncios en {country}",
        nationally: "A nivel nacional",
        globally: "A nivel mundial",
        // Categories
        cat_all: "Todos",
        cat_electronics: "Electrónica",
        cat_mining_gear: "Equipo de minería",
        cat_home_garden: "Hogar y Jardín",
        cat_sports_bikes: "Deportes y Bicis",
        cat_tools: "Herramientas",
        cat_games_hobbies: "Juegos y Aficiones",
        cat_furniture: "Muebles",
        cat_services: "Servicios",
        // Menu
        menu_display_prices_in: "Mostrar precios en:",
        menu_display_theme: "Tema de visualización:",
        menu_layout_view: "Vista de diseño:",
        // Types (singular labels)
        selling: "Venta",
        looking_for: "Se busca",
        verified_tooltip: "El usuario ha verificado su identidad",
    },
    de: {
        search: "Suche",
        location: "Ort",
        change: "Ändern",
        change_ellipsis: "Ändern…",
        all_listings: "Alle Anzeigen",
        seller_listings: "Verkäuferanzeigen",
        buyer_listings: "Käuferanzeigen",
        category: "Kategorie",
        type: "Typ",
        price_sats: "Preis (sats)",
        price_btc: "Preis (BTC)",
        min: "Min",
        max: "Max",
        newest: "Neueste",
        oldest: "Älteste",
        highest_price: "Höchster Preis",
        lowest_price: "Niedrigster Preis",
        closest: "Nächste",
        no_results: "Keine Ergebnisse",
        try_different: "Versuche andere Suchbegriffe oder lösche Filter",
        apply: "Anwenden",
        clear_all: "Alle löschen",
        cancel: "Abbrechen",
        close: "Schließen",
        change_location: "Standort ändern",
        radius: "Radius",
        enter_city: "Stadt eingeben",
        title_hero_1: "Bessere Angebote,",
        title_hero_2: "nutze besseres Geld.",
        how_it_works: "So funktioniert's",
        pricing: "Preise",
        post_listing: "Anzeige einstellen",
        sign_in: "Anmelden",
        // Homepage
        subheading: "Der Bitcoin-native Marktplatz",
        search_placeholder: "Suche nach Fahrrädern, ASICs, Konsolen…",
        choose_location: "Ort wählen",
        my_location: "Mein Standort",
        featured: "Empfohlen",
        latest: "Neu",
        services_label: "Dienstleistungen",
        no_goods_match: "Keine Artikel entsprechen deiner Suche",
        try_widen_radius: "Vergrößere deinen Radius oder lösche Filter",
        no_services_match: "Keine Dienstleistungen entsprechen deiner Suche",
        try_adjust_filters: "Versuche, deine Filter anzupassen",
        loading_more: "Lade mehr…",
        no_more_results: "Keine weiteren Ergebnisse",
        selected_location: "Ausgewählter Ort",
        set_location: "Ort festlegen",
        within_km_of: "Im Umkreis von {n} km von",
        // Footer
        legal: "Rechtliches",
        services: "Dienstleistungen",
        company: "Unternehmen",
        prohibited_items: "Verbotene Artikel",
        safety_tips: "Sicherheitstipps",
        safety: "Sicherheit",
        terms: "Bedingungen",
        privacy: "Datenschutz",
        escrow: "Treuhand",
        about: "Über uns",
        contact: "Kontakt",
        careers: "Karriere",
        help: "Hilfe",
        faq: "FAQ",
        support: "Support",
        made_with_love: "Gemacht mit ❤️ in Kanada",
        footer_tagline: "⚡ Bitboard — In‑App‑Chat + Lightning‑Treuhand. Halte die Korrespondenz in der App; Kontakt außerhalb der App verstößt gegen unsere Richtlinien.",
        // Notifications
        notifications: "Benachrichtigungen",
        all: "Alle",
        unread: "Ungelesen",
        messages: "Nachrichten",
        updates: "Aktualisierungen",
        system: "System",
        mark_as_read: "Als gelesen markieren",
        mark_all_as_read: "Alle als gelesen markieren",
        delete: "Löschen",
        view_details: "Details anzeigen",
        no_notifications: "Noch keine Benachrichtigungen",
        no_notifications_found: "Keine Benachrichtigungen gefunden",
        you_are_caught_up: "Du bist auf dem neuesten Stand! Keine Benachrichtigungen im Moment.",
        no_filter_notifications: "Keine {filter} Benachrichtigungen gefunden.",
        notification_summary: "Benachrichtigungsübersicht",
        total: "Gesamt",
        stay_updated: "Bleibe auf dem Laufenden über deine neuesten Nachrichten, Aktualisierungen und Systemankündigungen",
        view_all_notifications: "Alle Benachrichtigungen anzeigen",
        // Profile
        sign_out: "Abmelden",
        verified: "Verifiziert",
        reputation: "Ruf",
        excellent: "Ausgezeichnet",
        member_since: "Mitglied seit",
        active_listings: "Aktive Anzeigen",
        create_listing: "Anzeige erstellen",
        no_listings_yet: "Noch keine Anzeigen",
        no_listings_owner: "Du hast derzeit keine aktiven Anzeigen",
        no_listings_visitor: "Dieser Benutzer hat derzeit keine aktiven Anzeigen",
        grid: "Raster",
        list: "Liste",
        description: "Beschreibung",
        report_listing: "Anzeige melden",
        // Listing modal
        message_seller: "Nachricht an Verkäufer",
        send_message: "Nachricht senden",
        back_to_listing: "Zurück zum Inserat",
        share_listing: "Anzeige teilen",
        listing_warning: "Zur Sicherheit Korrespondenz in der App führen.",
        learn_more: "Mehr erfahren",
        results: "Ergebnisse",
        all_listings_globally: "Weltweit",
        all_of_country: "Alle Anzeigen in {country}",
        all_listings_in_country: "Alle Anzeigen in {country}",
        nationally: "Landesweit",
        globally: "Weltweit",
        // Categories
        cat_all: "Alle",
        cat_electronics: "Elektronik",
        cat_mining_gear: "Mining-Ausrüstung",
        cat_home_garden: "Haus & Garten",
        cat_sports_bikes: "Sport & Fahrräder",
        cat_tools: "Werkzeuge",
        cat_games_hobbies: "Spiele & Hobbys",
        cat_furniture: "Möbel",
        cat_services: "Dienstleistungen",
        // Menu
        menu_display_prices_in: "Preise anzeigen in:",
        menu_display_theme: "Anzeige-Thema:",
        menu_layout_view: "Layout-Ansicht:",
        // Types (singular labels)
        selling: "Verkauf",
        looking_for: "Suche",
        verified_tooltip: "Benutzer hat seine Identität verifiziert",
    },
};

let currentLang: Lang = 'en';
if (typeof window !== 'undefined') {
    try {
        const saved = localStorage.getItem('lang') as Lang | null;
        if (saved && dictionaries[saved]) currentLang = saved; else {
            const nav = navigator.language.toLowerCase();
            if (nav.startsWith('fr')) currentLang = 'fr';
            else if (nav.startsWith('es')) currentLang = 'es';
            else if (nav.startsWith('de')) currentLang = 'de';
        }
        document.documentElement.lang = currentLang;
    } catch { }
}

const listeners = new Set<() => void>();

function emit() {
    for (const l of Array.from(listeners)) l();
}

export function getLang(): Lang {
    return currentLang;
}

export function setLang(lang: Lang) {
    currentLang = lang;
    try { localStorage.setItem('lang', lang); } catch { }
    if (typeof document !== 'undefined') {
        document.documentElement.lang = lang;
    }
    emit();
}

export function subscribeLang(fn: () => void) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

export function t(key: string, lang?: Lang): string {
    const l = lang || currentLang;
    return dictionaries[l][key] ?? dictionaries.en[key] ?? key;
}

export function formatPostedAgo(ts: number, lang?: Lang): string {
    const l = lang || currentLang;
    const now = Date.now();
    const diffMs = Math.max(0, now - ts);
    const minutes = Math.floor(diffMs / (60 * 1000));
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));

    function en(): string {
        if (days >= 1) return `Posted ${days} day${days === 1 ? '' : 's'} ago`;
        if (hours >= 1) return `Posted ${hours} hour${hours === 1 ? '' : 's'} ago`;
        const m = Math.max(1, minutes);
        return `Posted ${m} minute${m === 1 ? '' : 's'} ago`;
    }

    function fr(): string {
        if (days >= 1) return `Publié il y a ${days} jour${days === 1 ? '' : 's'}`;
        if (hours >= 1) return `Publié il y a ${hours} heure${hours === 1 ? '' : 's'}`;
        const m = Math.max(1, minutes);
        return `Publié il y a ${m} minute${m === 1 ? '' : 's'}`;
    }

    function es(): string {
        if (days >= 1) return `Publicado hace ${days} día${days === 1 ? '' : 's'}`;
        if (hours >= 1) return `Publicado hace ${hours} hora${hours === 1 ? '' : 's'}`;
        const m = Math.max(1, minutes);
        return `Publicado hace ${m} minuto${m === 1 ? '' : 's'}`;
    }

    function de(): string {
        if (days >= 1) return `Veröffentlicht vor ${days} Tag${days === 1 ? '' : 'en'}`;
        if (hours >= 1) return `Veröffentlicht vor ${hours} Stunde${hours === 1 ? '' : 'n'}`;
        const m = Math.max(1, minutes);
        return `Veröffentlicht vor ${m} Minute${m === 1 ? '' : 'n'}`;
    }

    if (l === 'fr') return fr();
    if (l === 'es') return es();
    if (l === 'de') return de();
    return en();
}

// Explicit named exports for bundlers
export { dictionaries as _dict_debug };


